import React, { useEffect, useState } from 'react';

interface WeatherData {
  daily: {
    time: string[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    weathercode: number[];
  };
  hourly: {
    windspeed_10m: number[];
  };
}

interface Location {
  latitude: number;
  longitude: number;
  city: string;
}

interface WeatherDay {
  date: string;
  maxTemp: number;
  minTemp: number;
  weathercode: number;
}

const WeatherWidget: React.FC = () => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [selectedDay, setSelectedDay] = useState<number>(0);
  const [location, setLocation] = useState<Location>({
    latitude: 41.339,
    longitude: -8.560,
    city: 'Carregando...'
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocoding para obter o nome da cidade
          try {
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=pt`
            );
            const data = await response.json();
            setLocation({
              latitude,
              longitude,
              city: data.city || data.locality || 'Localização Desconhecida'
            });
          } catch (error) {
            console.error('Error getting location name:', error);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }, []);

  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${location.latitude}&longitude=${location.longitude}&daily=temperature_2m_max,temperature_2m_min,weathercode&hourly=windspeed_10m&timezone=Europe%2FLondon`
        );
        const data = await response.json();
        setWeatherData(data);
      } catch (error) {
        console.error('Error fetching weather data:', error);
      }
    };

    fetchWeatherData();
  }, [location]);

  const getWeatherIcon = (code: number) => {
    switch (code) {
      case 0: // Clear sky
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="5" fill="#FFD700"/>
            <path d="M12 3V5M12 19V21M5 12H3M21 12H19M18.4 5.6L16.9 7.1M7.1 16.9L5.6 18.4M18.4 18.4L16.9 16.9M7.1 7.1L5.6 5.6" stroke="#FFD700" strokeWidth="2"/>
          </svg>
        );
      case 1:
      case 2:
      case 3: // Partly cloudy
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="4" fill="#FFD700"/>
            <path d="M6 16H16C18.2091 16 20 14.2091 20 12C20 9.79086 18.2091 8 16 8H15.5C15.2239 8 15 7.77614 15 7.5C15 5.567 13.433 4 11.5 4C9.567 4 8 5.567 8 7.5C8 7.77614 7.77614 8 7.5 8H6C3.79086 8 2 9.79086 2 12C2 14.2091 3.79086 16 6 16Z" fill="#E5E7EB"/>
          </svg>
        );
      case 51:
      case 53:
      case 55:
      case 61:
      case 63:
      case 65: // Rain
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 16H16C18.2091 16 20 14.2091 20 12C20 9.79086 18.2091 8 16 8H15.5C15.2239 8 15 7.77614 15 7.5C15 5.567 13.433 4 11.5 4C9.567 4 8 5.567 8 7.5C8 7.77614 7.77614 8 7.5 8H6C3.79086 8 2 9.79086 2 12C2 14.2091 3.79086 16 6 16Z" fill="#E5E7EB"/>
            <path d="M8 16L7 19M12 16L11 19M16 16L15 19" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      default: // Cloudy
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 16H16C18.2091 16 20 14.2091 20 12C20 9.79086 18.2091 8 16 8H15.5C15.2239 8 15 7.77614 15 7.5C15 5.567 13.433 4 11.5 4C9.567 4 8 5.567 8 7.5C8 7.77614 7.77614 8 7.5 8H6C3.79086 8 2 9.79086 2 12C2 14.2091 3.79086 16 6 16Z" fill="#E5E7EB"/>
          </svg>
        );
    }
  };

  const getDayName = (dateStr: string, index: number) => {
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const date = new Date(dateStr);
    if (index === 0) return 'Hoje';
    if (index === 1) return 'Amanhã';
    return days[date.getDay()];
  };

  const getWeatherDescription = (code: number) => {
    if (code === 0) return 'Céu limpo, dia perfeito para atividades ao ar livre';
    if (code === 1 || code === 2 || code === 3) return 'Parcialmente nublado';
    if (code >= 51 && code <= 67) return 'Chuva';
    if (code >= 71 && code <= 77) return 'Neve';
    if (code >= 80 && code <= 82) return 'Aguaceiros';
    return 'Nublado';
  };

  if (!weatherData) return <div>Carregando...</div>;

  const currentWindSpeed = Math.round(weatherData.hourly.windspeed_10m[0]);
  const selectedDayData: WeatherDay = {
    date: weatherData.daily.time[selectedDay],
    maxTemp: weatherData.daily.temperature_2m_max[selectedDay],
    minTemp: weatherData.daily.temperature_2m_min[selectedDay],
    weathercode: weatherData.daily.weathercode[selectedDay]
  };

  return (
    <div className="bg-gradient-to-r from-[#1a3b5d] to-[#a4c639] rounded-lg overflow-hidden">
      <div className="flex justify-between items-center p-4 text-white">
        <h2 className="text-xl font-semibold">Previsão do Tempo</h2>
        <div className="flex items-center gap-1">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span>{location.city}</span>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 px-4 mb-4">
        {weatherData.daily.time.slice(0, 7).map((date, index) => (
          <div
            key={date}
            onClick={() => setSelectedDay(index)}
            className={`cursor-pointer text-center p-2 rounded-lg ${
              index === selectedDay ? 'bg-[#ffffff33]' : ''
            }`}
          >
            <div className="text-white text-sm mb-1">{getDayName(date, index)}</div>
            <div className="w-8 h-8 mx-auto mb-1">
              {getWeatherIcon(weatherData.daily.weathercode[index])}
            </div>
            <div className="text-white font-bold">
              {Math.round(weatherData.daily.temperature_2m_max[index])}°
            </div>
            <div className="text-white/80 text-sm">
              {Math.round(weatherData.daily.temperature_2m_min[index])}°
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#ffffff0d] p-4 rounded-t-lg">
        <h3 className="text-white text-lg font-semibold mb-3">
          Detalhes para {selectedDay === 0 ? 'Hoje' : getDayName(selectedDayData.date, selectedDay)}
        </h3>
        <div className="grid grid-cols-3 gap-4 text-white mb-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3v10.55c-1.23.59-2 1.88-2 3.45 0 2.21 1.79 4 4 4s4-1.79 4-4c0-1.57-.77-2.86-2-3.45V3h-4z"/>
            </svg>
            <span>Máxima: {Math.round(selectedDayData.maxTemp)}°C</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
            </svg>
            <span>Mínima: {Math.round(selectedDayData.minTemp)}°C</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3 1.35-3 3-3 3 1.35 3 3zm-3-15C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 11.5 2zM17 12c0 2.76-2.24 5-5 5s-5-2.24-5-5 2.24-5 5-5 5 2.24 5 5z"/>
            </svg>
            <span>Vento: {currentWindSpeed} km/h</span>
          </div>
        </div>
        <div className="text-white/80 text-sm">
          {getWeatherDescription(selectedDayData.weathercode)}
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;
